import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useCameraStore } from '../../store/cameraStore';
import { useRaspiStore } from '../../store/raspiStore';
import { sendCameraStreamCommand } from '../../services/api';
import websocketService from '../../services/websocket';
import { formatUtcToLocalTime } from '@/utils/timeUtils';

// Low-latency MJPEG path runs first. The Pi's MJPEG server is plain http on
// port 8090; loading it straight (http://<pi>:8090/...) inside our https app
// is blocked by the browser as mixed content, which used to push EVERY camera
// onto the slow MQTT-relay fallback. So we go through a same-origin nginx
// reverse proxy (/camstream/<pi_ip>/<idx>/stream.mjpg → http://<pi>:8090/...);
// the browser sees an https same-origin request (allowed) and nginx does the
// http hop to the Pi. This restores the ~50-100ms feed AND works remotely
// (nginx runs on the lab LAN, so Tailscale/public-domain users get it too).
// If the proxied hit still fails within DIRECT_PROBE_TIMEOUT_MS, we fall back
// to the MQTT-relay path via the hub.
const DIRECT_PROBE_TIMEOUT_MS = 3500;

interface CameraViewDialogProps {
  open: boolean;
  onClose: () => void;
  cameraId: string;
  raspiId: string;
}

// Static registry to track active instances per camera
const activeInstances = new Map<string, string>();

const CameraViewDialog: React.FC<CameraViewDialogProps> = ({
  open,
  onClose,
  cameraId,
  raspiId
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isStreamActive, setIsStreamActive] = useState(false);
  // 'unknown' = still probing; 'direct' = lab LAN MJPEG works; 'mqtt' = remote,
  // fall back to hub-relay. Determined by trying to load the direct URL once
  // and timing out / catching onerror.
  const [streamMode, setStreamMode] = useState<'unknown' | 'direct' | 'mqtt'>('unknown');
  const [directUrl, setDirectUrl] = useState<string | null>(null);
  // Use selector to always get the latest camera object and re-render on updates
  const camera = useCameraStore((state) => state.allCameras[cameraId]);

  // Instance tracking to prevent cross-instance cleanup
  const instanceId = useRef<string>(`${cameraId}_${Date.now()}_${Math.random()}`);
  const isActiveInstance = useRef<boolean>(false);
  // Mirror isStreamActive into a ref so the cleanup closure sees the
  // current value, not the stale value captured at effect-registration
  // time. Without this, fast open→stream→close cycles would skip
  // stopCameraStream and leave a ghost stream running on the RASPI.
  const isStreamActiveRef = useRef<boolean>(false);
  useEffect(() => { isStreamActiveRef.current = isStreamActive; }, [isStreamActive]);

  useEffect(() => {
    if (open && cameraId && raspiId) {
      // Check if another instance is already active for this camera
      const currentActiveInstance = activeInstances.get(cameraId);
      if (currentActiveInstance && currentActiveInstance !== instanceId.current) {
        // Another instance is active, don't proceed
        return;
      }

      // Mark this instance as active
      isActiveInstance.current = true;
      activeInstances.set(cameraId, instanceId.current);

      // Probe direct LAN MJPEG first. If it loads within the timeout,
      // we render that <img> directly (low latency, calibration use case).
      // If it errors or times out, fall back to MQTT-relay via hub.
      const raspiNow = useRaspiStore.getState().allRaspis[raspiId];
      const cameraNow = useCameraStore.getState().allCameras[cameraId];
      // Same-origin proxied path (see nginx `location /camstream/`). The Pi
      // stream port (8090) lives in nginx, so we only need the Pi IP + camera
      // index here. calibration_stream_port presence still gates on the Pi
      // running new-enough code that publishes the stream capability.
      const directCandidate =
        raspiNow?.raspi_ip &&
        cameraNow?.camera_index !== undefined &&
        raspiNow?.calibration_stream_port
          ? `/camstream/${raspiNow.raspi_ip}/${cameraNow.camera_index}/stream.mjpg`
          : null;

      if (directCandidate) {
        setDirectUrl(directCandidate);
        setIsLoading(true);
        const probe = new Image();
        let resolved = false;
        const t = setTimeout(() => {
          if (resolved) return;
          resolved = true;
          probe.src = '';  // cancel
          // Direct didn't load in time — user is probably remote. Fall back.
          setStreamMode('mqtt');
          startCameraStream();
          // Also subscribe to MQTT camera_stream (hub-relay path)
          websocketService.subscribeToCameraStream(cameraId);
        }, DIRECT_PROBE_TIMEOUT_MS);
        probe.onload = () => {
          if (resolved) return;
          resolved = true;
          clearTimeout(t);
          setStreamMode('direct');
          setIsStreamActive(true);
          setIsLoading(false);
          // No MQTT command needed; the Pi's MJPEG server starts the
          // shared reader on first HTTP subscriber automatically.
        };
        probe.onerror = () => {
          if (resolved) return;
          resolved = true;
          clearTimeout(t);
          setStreamMode('mqtt');
          startCameraStream();
          websocketService.subscribeToCameraStream(cameraId);
        };
        probe.src = directCandidate;
      } else {
        // Pi running old code (no camera_index / port published) — skip
        // direct probe and go straight to the legacy MQTT path.
        setStreamMode('mqtt');
        startCameraStream();
        websocketService.subscribeToCameraStream(cameraId);
      }
    }
    // Stop stream and unsubscribe on unmount/close
    const cleanupCameraId = cameraId;
    const cleanupRaspiId = raspiId;
    return () => {
      if (isActiveInstance.current) {
        // Use the ref to get the current stream-active state — the
        // closure-captured `isStreamActive` is stale.
        if (isStreamActiveRef.current && cleanupRaspiId && cleanupCameraId) {
          // Best-effort STOP to the RASPI so it stops capturing frames.
          // Only meaningful for the MQTT-relay path; the direct MJPEG
          // path auto-stops when the <img> connection closes (refcounted
          // CameraReader on the Pi releases /dev/videoN at refcount=0).
          sendCameraStreamCommand(cleanupRaspiId, cleanupCameraId, false).catch(err => {
            console.error('Error stopping camera stream on cleanup:', err);
          });
        }
        // Always unsubscribe — idempotent on the hub side, and this
        // closes the "ghost subscription" leak when navigation happens
        // before the dialog finishes mounting / streaming.
        if (cleanupCameraId) {
          websocketService.unsubscribeFromCameraStream(cleanupCameraId);
        }
        // Remove from active instances registry
        if (activeInstances.get(cleanupCameraId) === instanceId.current) {
          activeInstances.delete(cleanupCameraId);
        }
        isActiveInstance.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cameraId, raspiId]);

  const startCameraStream = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const success = await sendCameraStreamCommand(raspiId, cameraId, true);
      if (success) {
        setIsStreamActive(true);
      } else {
        setError('Failed to start camera stream');
      }
    } catch (err) {
      setError('Error starting camera stream');
      console.error('Error starting camera stream:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const stopCameraStream = async () => {
    try {
      await sendCameraStreamCommand(raspiId, cameraId, false);
      setIsStreamActive(false);
    } catch (err) {
      console.error('Error stopping camera stream:', err);
    }
  };

  const handleClose = () => {
    if (isStreamActive) {
      stopCameraStream();
    }
    // Remove from active instances registry
    if (activeInstances.get(cameraId) === instanceId.current) {
      activeInstances.delete(cameraId);
    }
    // Mark instance as inactive before closing
    isActiveInstance.current = false;
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '60vh',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
          Camera Stream: {cameraId}
        </Typography>
        <Button
          onClick={handleClose}
          startIcon={<CloseIcon />}
          variant="outlined"
          size="small"
        >
          Close
        </Button>
      </DialogTitle>
      
      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
            {error}
          </Alert>
        )}
        
        {isLoading ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            gap: 2
          }}>
            <CircularProgress size={60} />
            <Typography variant="body1" color="text.secondary">
              {streamMode === 'unknown'
                ? 'Probing low-latency stream...'
                : 'Starting camera stream...'}
            </Typography>
          </Box>
        ) : streamMode === 'direct' && directUrl ? (
          // Low-latency path: browser loads the MJPEG (multipart/x-mixed-
          // replace) same-origin via the nginx /camstream proxy to the Pi —
          // native browser rendering, no base64 decode, no MQTT hop.
          // Latency ~50-100ms total, and works remotely too (proxy runs on
          // the lab LAN).
          <Box sx={{
            width: '100%',
            height: '400px',
            border: '2px dashed #ccc',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            position: 'relative'
          }}>
            <img
              src={directUrl}
              alt={`Live calibration stream from ${cameraId}`}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            <Box sx={{
              position: 'absolute', top: 8, right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.7)', color: 'white',
              padding: '4px 8px', borderRadius: 1, fontSize: '0.75rem'
            }}>
              Live (low-latency)
            </Box>
          </Box>
        ) : isStreamActive && camera?.currentFrame ? (
          <Box sx={{
            width: '100%',
            height: '400px',
            border: '2px dashed #ccc',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            position: 'relative'
          }}>
            <img
              src={`data:image/jpeg;base64,${camera.currentFrame}`}
              alt={`Live stream from ${cameraId}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
            {/* Status overlay */}
            <Box sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: 1,
              fontSize: '0.75rem'
            }}>
              Remote (via hub) · Frame: {camera?.camera_image_counter || 0}
              {camera?.last_update && (
                <div>Last: {formatUtcToLocalTime(camera.last_update)}</div>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ 
            width: '100%',
            height: '400px',
            border: '2px dashed #ccc',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5'
          }}>
            <Typography variant="body1" color="text.secondary">
              {isStreamActive ? 'Waiting for camera frames...' : 'Camera stream not available'}
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CameraViewDialog; 